import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserConfiguration from './user-configuration/UserConfiguration';
import { Privileged } from '../hocs/withPrivileges';
import MenuItem from './MenuItem';
import Privileges from '../utils/Privileges';
import './_styles_/LeftMenu.css';
import { getUserIdFromToken, getUserNameFromToken } from '../utils/authentication';
import { getAccessToken } from '../reducers';
import { extractFirstLetters, onActionKey } from '../utils';
import Logo from './Logo';
import ConfirmButton from './buttons/ConfirmButton';
import { logoutUser } from '../actions/authentication';
import ChangePasswordButton from './buttons/ChangePasswordButton';
import GeneralReportsMenu from './reports/GeneralReportsMenu';
import ContactsPopup from './contacts/ContactsPopup';
import useAuthentication from '../hooks/useAuthentication';
import EnvTitle from './EnvTitle';

function LeftMenu() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useApolloClient();
    const accessToken = useSelector(getAccessToken);
    const { isInternalUser } = useAuthentication();
    const userId = getUserIdFromToken(accessToken);
    const userName = getUserNameFromToken(accessToken);
    const userInitials = extractFirstLetters(userName).toUpperCase();
    const [isGlobalMenuActive, setGlobalMenuActive] = useState(true);
    const toggleGlobalMenu = () => setGlobalMenuActive(!isGlobalMenuActive);
    const [isAdminMenuActive, setAdminMenuActive] = useState(true);
    const toggleAdminMenu = () => setAdminMenuActive(!isAdminMenuActive);
    const onLogoutHandler = () => {
        dispatch(logoutUser(t('logout.logout-success'), t('logout.logout-error'), client));
    };
    const [isReportsMenuVisible, setReportsMenuVisible] = useState(false);
    const onReportsClickHandler = () => setReportsMenuVisible(true);
    const onReportsHideHandler = () => setReportsMenuVisible(false);
    const [isContactsPopupVisible, setContactsPopupVisible] = useState(false);
    const onContactsClickHandler = () => setContactsPopupVisible(true);
    const onContactsHideHandler = () => setContactsPopupVisible(false);
    const [isUserConfigurationVisible, setUserConfigurationVisible] = useState(false);
    const onUserClickHandler = () => setUserConfigurationVisible(true);
    const onUserHideHandler = () => setUserConfigurationVisible(false);
    const adminSeparatorSufficientPrivileges = [
        Privileges.SETTINGS_EDIT,
        Privileges.AUCTION_LIMIT_EDIT,
        Privileges.AUCTION_LIMIT_VIEW,
        Privileges.AUCTION_TEMPLATE_EDIT,
        Privileges.AUCTION_TEMPLATE_VIEW,
        Privileges.AUCTIONEER_DATA_TEMPLATE_EDIT,
        Privileges.CASH_LIMIT_VIEW,
        Privileges.CASH_LIMIT_EDIT,
        Privileges.MANUAL_CASH_LIMIT_VIEW,
        Privileges.MANUAL_CASH_LIMIT_EDIT,
        Privileges.CLEARING_HOUSE_EDIT,
        Privileges.CLEARING_HOUSE_VIEW,
        Privileges.COUNTRY_EDIT,
        Privileges.CURRENCY_EDIT,
        Privileges.MEMBER_EDIT,
        Privileges.MEMBER_VIEW,
        Privileges.PRODUCT_EDIT,
        Privileges.PRODUCT_VIEW,
        Privileges.USER_EDIT,
        Privileges.USER_VIEW,
        Privileges.ZONE_EDIT_ALL,
        Privileges.ZONE_VIEW_ALL,
        Privileges.ZONE_VIEW_OWN,
        Privileges.HOLIDAY_EDIT,
    ];
    return (
        <div className="left-menu">
            <div className="left-menu--top">
                <Logo className="left-menu--logo-container" />
                <div className="left-menu--separator">
                    <div
                        className={`left-menu--toggle-global-menu-handle left-menu--separator--handle${isGlobalMenuActive ? ' active' : ''}`}
                        onClick={toggleGlobalMenu}
                        onKeyUp={onActionKey(toggleGlobalMenu)}
                        role="button"
                        tabIndex="0"
                        aria-label={t('left-menu.toggle-global-menu')}
                        title={t('left-menu.toggle-global-menu')}
                    >
                        <FontAwesomeIcon icon="ellipsis-h" />
                    </div>
                </div>
                {isGlobalMenuActive && (
                    <>
                        <Privileged sufficientPrivileges={[Privileges.AUCTION_VIEW_ALL, Privileges.AUCTION_VIEW_OWN, Privileges.AUCTION_EDIT]}>
                            <MenuItem to="/auctions" className="left-menu--item left-menu--auctions">
                                <FontAwesomeIcon icon="gavel" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.auctions')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.REPORT_VIEW]}>
                            <div
                                className="left-menu--item left-menu--reports"
                                onClick={onReportsClickHandler}
                                onKeyUp={onActionKey(onReportsClickHandler)}
                                role="button"
                                tabIndex="0"
                            >
                                <FontAwesomeIcon icon="newspaper" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.reports')}</span>
                            </div>
                            {isReportsMenuVisible && <GeneralReportsMenu onCancel={onReportsHideHandler} />}
                        </Privileged>
                        <MenuItem to="/messages" className="left-menu--item left-menu--messages">
                            <FontAwesomeIcon icon="envelope" className="left-menu--item--icon" />
                            <span className="left-menu--item--text">{t('left-menu.messages')}</span>
                        </MenuItem>
                    </>
                )}
                <Privileged sufficientPrivileges={adminSeparatorSufficientPrivileges}>
                    <div className="left-menu--separator">
                        <div
                            className={`left-menu--toggle-admin-menu-handle left-menu--separator--handle${isAdminMenuActive ? ' active' : ''}`}
                            onClick={toggleAdminMenu}
                            onKeyUp={onActionKey(toggleAdminMenu)}
                            role="button"
                            tabIndex="0"
                            aria-label={t('left-menu.toggle-admin-menu')}
                            title={t('left-menu.toggle-admin-menu')}
                        >
                            <FontAwesomeIcon icon="ellipsis-h" />
                        </div>
                    </div>
                </Privileged>
                {isAdminMenuActive && (
                    <>
                        <Privileged sufficientPrivileges={[Privileges.SETTINGS_EDIT]}>
                            <MenuItem to="/settings" className="left-menu--item left-menu--settings">
                                <FontAwesomeIcon icon="cog" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.settings')}</span>
                            </MenuItem>
                            <div className="left-menu--separator" />
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.AUCTION_LIMIT_EDIT, Privileges.AUCTION_LIMIT_VIEW]}>
                            <MenuItem to="/auction-limits" className="left-menu--item left-menu--auction-limits">
                                <span className="fa-layers fa-fw left-menu--item--icon">
                                    <FontAwesomeIcon icon="gavel" transform="shrink-7 right-6 down-2" />
                                    <FontAwesomeIcon icon="filter" transform="left-2" />
                                </span>
                                <span className="left-menu--item--text">{t('left-menu.auction-limits')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.AUCTION_TEMPLATE_EDIT, Privileges.AUCTION_TEMPLATE_VIEW]}>
                            <MenuItem to="/auction-templates" className="left-menu--item left-menu--auction-templates">
                                <span className="fa-layers fa-fw left-menu--item--icon">
                                    <FontAwesomeIcon icon="gavel" transform="shrink-7 right-8 down-2" />
                                    <FontAwesomeIcon icon="file-alt" transform="left-4" />
                                </span>
                                <span className="left-menu--item--text">{t('left-menu.auction-templates')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.AUCTIONEER_DATA_TEMPLATE_EDIT]}>
                            <MenuItem to="/auctioneer-data-templates" className="left-menu--item left-menu--auctioneer-data-templates">
                                <FontAwesomeIcon icon="file-alt" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.auctioneer-data-templates')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.CASH_LIMIT_EDIT, Privileges.CASH_LIMIT_VIEW]}>
                            <MenuItem to="/cash-limit-upload-events" className="left-menu--item left-menu--cash-limit-upload-events">
                                <FontAwesomeIcon icon="file-invoice-dollar" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.cash-limit-upload-events')}</span>
                            </MenuItem>
                            <MenuItem to="/cash-limits" className="left-menu--item left-menu--cash-limits">
                                <FontAwesomeIcon icon="funnel-dollar" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.cash-limits')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.CLEARING_HOUSE_EDIT, Privileges.CLEARING_HOUSE_VIEW]}>
                            <MenuItem to="/clearing-houses" className="left-menu--item left-menu--clearing-houses">
                                <FontAwesomeIcon icon="balance-scale" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.clearing-houses')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.COUNTRY_EDIT]}>
                            <MenuItem to="/countries" className="left-menu--item left-menu--countries">
                                <FontAwesomeIcon icon="globe" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.countries')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.CURRENCY_EDIT]}>
                            <MenuItem to="/currencies" className="left-menu--item left-menu--currencies">
                                <FontAwesomeIcon icon="coins" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.currencies')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.HOLIDAY_EDIT]}>
                            <MenuItem to="/holidays" className="left-menu--item left-menu--holidays">
                                <FontAwesomeIcon icon="umbrella-beach" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.holidays')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.MANUAL_CASH_LIMIT_EDIT, Privileges.MANUAL_CASH_LIMIT_VIEW]}>
                            <MenuItem to="/manual-cash-limits" className="left-menu--item left-menu--manual-cash-limits">
                                <span className="fa-layers fa-fw left-menu--item--icon">
                                    <FontAwesomeIcon icon="funnel-dollar" transform="shrink-8 up-3" />
                                    <FontAwesomeIcon icon="hand-holding" />
                                </span>
                                <span className="left-menu--item--text">{t('left-menu.manual-cash-limits')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.MEMBER_EDIT, Privileges.MEMBER_VIEW]}>
                            <MenuItem to="/members" className="left-menu--item left-menu--members">
                                <FontAwesomeIcon icon="building" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.members')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.PRODUCT_EDIT, Privileges.PRODUCT_VIEW]}>
                            <MenuItem to="/products" className="left-menu--item left-menu--products">
                                <FontAwesomeIcon icon="boxes" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.products')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.USER_EDIT, Privileges.USER_VIEW]}>
                            <MenuItem to="/users" className="left-menu--item left-menu--users">
                                <FontAwesomeIcon icon="users" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.users')}</span>
                            </MenuItem>
                        </Privileged>
                        <Privileged sufficientPrivileges={[Privileges.ZONE_EDIT_ALL, Privileges.ZONE_VIEW_ALL, Privileges.ZONE_VIEW_OWN]}>
                            <MenuItem to="/zones" className="left-menu--item left-menu--zones">
                                <FontAwesomeIcon icon="map-marked-alt" className="left-menu--item--icon" />
                                <span className="left-menu--item--text">{t('left-menu.zones')}</span>
                            </MenuItem>
                        </Privileged>
                    </>
                )}
            </div>
            <div className="left-menu--bottom">
                <div className="left-menu--bottom--item left-menu--user--container">
                    <div
                        className="left-menu--user"
                        title={`${t('left-menu.open-user-configuration')} - ${userName} (${userId})`}
                        onClick={onUserClickHandler}
                        onKeyUp={onActionKey(onUserClickHandler)}
                        role="button"
                        tabIndex="0"
                    >
                        <FontAwesomeIcon icon="cog" className="left-menu--item--icon" />
                        <div className="left-menu--user--initials">
                            <span>{userInitials}</span>
                        </div>
                        <span className="left-menu--item--text left-menu--user--user-id">{userId}</span>
                    </div>
                    {isUserConfigurationVisible && <UserConfiguration onCancel={onUserHideHandler} />}
                </div>
                <div className="left-menu--bottom--item">
                    <ConfirmButton
                        className="logout-button"
                        onConfirm={onLogoutHandler}
                        message={t('logout.confirm-logout')}
                        title={t('left-menu.logout')}
                    >
                        <FontAwesomeIcon icon="power-off" />
                    </ConfirmButton>
                    <div
                        className="contacts"
                        onClick={onContactsClickHandler}
                        onKeyUp={onActionKey(onContactsClickHandler)}
                        role="button"
                        aria-label={t('left-menu.contacts')}
                        tabIndex="0"
                    >
                        <FontAwesomeIcon icon="address-book" title={t('left-menu.contacts')} />
                    </div>
                    {isContactsPopupVisible && <ContactsPopup onCancel={onContactsHideHandler} />}
                    {!isInternalUser && <ChangePasswordButton />}
                </div>
                <EnvTitle />
            </div>
        </div>
    );
}

export default LeftMenu;
